import type { PowerSource, StandardEquipmentCatalogue } from "../../../configurator";
import type {
  MeasurementLogFormValues,
  MeasurementRecord,
  SavedCoilConfiguration,
} from "../../../web-app";

function formatPowerSourceShortName(name: string): string {
  return name.replace(/\s+power source$/i, "");
}

function powerSourceForConfiguration(
  config: SavedCoilConfiguration,
  catalogue: StandardEquipmentCatalogue,
): PowerSource | undefined {
  return catalogue.powerSources.find((powerSource) => powerSource.id === config.selectedPowerSourceId);
}

export function emptyMeasurementFormValues(): MeasurementLogFormValues {
  return {
    configurationId: "",
    powerSourceVoltageV: "",
    powerSourceCurrentA: "",
    powerFactor: "",
    measuredTemperatureC: "",
    measuredHeatingCycleTimeMinutes: "",
    measuredCooldownTimeMinutes: "",
    notes: "",
  };
}

export function measurementToFormValues(record: MeasurementRecord): MeasurementLogFormValues {
  return {
    configurationId: record.configurationId,
    powerSourceVoltageV: String(record.powerSourceVoltageV),
    powerSourceCurrentA: String(record.powerSourceCurrentA),
    powerFactor: String(record.powerFactor),
    measuredTemperatureC: String(record.measuredTemperatureC),
    measuredHeatingCycleTimeMinutes:
      record.measuredHeatingCycleTimeMinutes != null
        ? String(record.measuredHeatingCycleTimeMinutes)
        : "",
    measuredCooldownTimeMinutes:
      record.measuredCooldownTimeMinutes != null
        ? String(record.measuredCooldownTimeMinutes)
        : "",
    notes: record.notes,
  };
}

export function buildMeasurementRecord(
  values: MeasurementLogFormValues,
  configurations: SavedCoilConfiguration[],
  catalogue: StandardEquipmentCatalogue,
  existingRecord?: MeasurementRecord,
): MeasurementRecord | null {
  const config = configurations.find((item) => item.id === values.configurationId);

  if (config === undefined && existingRecord === undefined) {
    return null;
  }

  const voltage = Number(values.powerSourceVoltageV);
  const current = Number(values.powerSourceCurrentA);
  const pf = Number(values.powerFactor);
  const measuredCycle =
    values.measuredHeatingCycleTimeMinutes.trim() !== "" &&
    Number.isFinite(Number(values.measuredHeatingCycleTimeMinutes))
      ? Number(values.measuredHeatingCycleTimeMinutes)
      : undefined;
  const measuredCooldown =
    values.measuredCooldownTimeMinutes.trim() !== "" &&
    Number.isFinite(Number(values.measuredCooldownTimeMinutes))
      ? Number(values.measuredCooldownTimeMinutes)
      : undefined;

  const powerSource = config === undefined ? undefined : powerSourceForConfiguration(config, catalogue);
  const powerSourceLabel =
    config === undefined
      ? existingRecord?.powerSourceLabel
      : powerSource === undefined
        ? config.selectedPowerSourceId
        : formatPowerSourceShortName(powerSource.name);

  return {
    id: existingRecord?.id ?? `meas-${Date.now()}`,
    configurationId: config?.id ?? existingRecord?.configurationId ?? values.configurationId,
    configurationReference:
      config?.reference ?? existingRecord?.configurationReference ?? values.configurationId,
    ...(powerSourceLabel === undefined ? {} : { powerSourceLabel }),
    ...(config === undefined
      ? existingRecord?.powerSourceFrequencyHz === undefined
        ? {}
        : { powerSourceFrequencyHz: existingRecord.powerSourceFrequencyHz }
      : powerSource === undefined
        ? {}
        : { powerSourceFrequencyHz: powerSource.powerSourceFrequencyHz }),
    powerSourceVoltageV: voltage,
    powerSourceCurrentA: current,
    powerFactor: pf,
    measuredTemperatureC: Number(values.measuredTemperatureC),
    ...(measuredCycle !== undefined ? { measuredHeatingCycleTimeMinutes: measuredCycle } : {}),
    ...(measuredCooldown !== undefined ? { measuredCooldownTimeMinutes: measuredCooldown } : {}),
    predictedHeatingCycleTimeMinutes:
      config?.recommendation.heatingCycleTimeMinutes ??
      existingRecord?.predictedHeatingCycleTimeMinutes ??
      null,
    predictedDeliveredPowerKw:
      config?.recommendation.deliveredPowerKw ?? existingRecord?.predictedDeliveredPowerKw ?? null,
    measuredDeliveredPowerKw: (voltage * current * pf) / 1000,
    notes: values.notes,
    inFit: existingRecord?.inFit ?? true,
    loggedAt: existingRecord?.loggedAt ?? new Date(),
  };
}
